import { itemScopeApplies, type GeneratedDimension, type GeneratedItem, type GeneratedStatus } from "@keydials/api-client";

/**
 * KEY-512. One dot per scoring axis, for an item and for the action point above it.
 *
 * An itemised dial can score an item on several axes, each with its own palette, and until now the UI showed
 * a single dot for all of them — the least-advanced status across the item's gating axes. That reads as one
 * opinion where there are several, and everything the other axes say only appears once the card is expanded.
 *
 * The segment half restates {@link AxisStatusRollup.Resolve}
 * (`src/api/KeyDials.Domain/Instances/AxisStatusRollup.cs`) for every axis rather than the one the dial names,
 * with each of its rules called out below. It is a read-path derivation like its server counterpart: nothing
 * here is stored, so it cannot recurse or race a write.
 */

/** Unset grey — StatusPicker's UNSET, the colour an unscored axis has always shown. */
export const UNSET_COLOUR = "#94a3b8";

/** Index key for {@link ItemAxisLookup}; the same shape DialWorkspace builds the map with. */
export const itemStatusKeyOf = (sectionKey: string, segmentKey: string, itemKey: string, dimensionKey: string) =>
  `${sectionKey}|${segmentKey}|${itemKey}|${dimensionKey}`;

/** (section, segment, item, dimension) → the status key that item sits at on that axis. */
export type ItemAxisLookup = ReadonlyMap<string, string>;

/** One axis's current standing, ready to render as a dot. */
export interface AxisDot {
  /** The axis's key: stable across renders and unique within a run. */
  key: string;
  /** The axis's name, for the tooltip — a dot alone can't say which axis it is. */
  label: string;
  /** The status's name, or null when this axis has nothing to show yet. */
  statusName: string | null;
  colour: string;
  /**
   * True for an axis that doesn't gate completion. Named in the tooltip the same way the card's own list
   * names it, so a low dot beside a "Complete" pill reads as an optional axis rather than a contradiction.
   */
  optional: boolean;
}

/**
 * The axes that score one item, in the order the dial lists them.
 *
 * A dimension can be scoped to specific items, so an item is scored by its own axes only — one item may track
 * a document while its neighbour carries a plain yes/no. Unscoped dimensions apply to everything.
 */
export function itemAxes(
  dimensions: GeneratedDimension[],
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): GeneratedDimension[] {
  return dimensions.filter((d) => itemScopeApplies(d.appliesToItemKeys, sectionKey, segmentKey, itemKey));
}

/**
 * One dot per axis that scores this item — including the optional (non-gating) ones, so the collapsed row
 * lists exactly the axes the expanded Progress pane does. The dots are per-axis statuses, not an aggregate,
 * so they don't compete with the item's Complete / N/A pill: that keeps its own gating rule.
 */
export function itemAxisDots(
  dimensions: GeneratedDimension[],
  statuses: ItemAxisLookup,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): AxisDot[] {
  return itemAxes(dimensions, sectionKey, segmentKey, itemKey).map((dim) => {
    const status = statusOn(dim, statuses.get(itemStatusKeyOf(sectionKey, segmentKey, itemKey, dim.key)));
    return dotFor(dim, status);
  });
}

/**
 * One dot per axis that scores at least one item in this action point, each showing the **worst** status its
 * items sit at on that axis — "this action point is as good as its worst item", the reading
 * {@link AxisStatusRollup} already gives the one axis a dial derives its colour from.
 */
export function segmentAxisDots(
  dimensions: GeneratedDimension[],
  statuses: ItemAxisLookup,
  sectionKey: string,
  segmentKey: string,
  items: GeneratedItem[],
): AxisDot[] {
  const axes = dimensions.filter((d) =>
    items.some((item) => itemScopeApplies(d.appliesToItemKeys, sectionKey, segmentKey, item.key)));
  // AxisStatusRollup is handed its items with the N/A ones already dropped: an item nobody is scoring says
  // nothing about the action point, on any axis.
  const live = items.filter((item) => !isItemExcluded(dimensions, statuses, sectionKey, segmentKey, item.key));
  return axes.map((dim) => dotFor(dim, worstOnAxis(dim, statuses, sectionKey, segmentKey, live)));
}

function worstOnAxis(
  dim: GeneratedDimension,
  statuses: ItemAxisLookup,
  sectionKey: string,
  segmentKey: string,
  items: GeneratedItem[],
): GeneratedStatus | undefined {
  let worst: GeneratedStatus | undefined;
  for (const item of items) {
    // An axis that doesn't reach the item must not be answered by it. This is also what tells "not scored on
    // this axis" apart from "not scored by this axis at all" — the lookup can't.
    if (!itemScopeApplies(dim.appliesToItemKeys, sectionKey, segmentKey, item.key)) {
      continue;
    }
    const statusKey = statuses.get(itemStatusKeyOf(sectionKey, segmentKey, item.key, dim.key));
    if (statusKey === undefined) {
      continue; // absent means "not scored on this axis yet", which is not an opinion about the segment
    }
    const status = dim.palette.statuses.find((s) => s.key === statusKey);
    // A status the palette no longer holds — a key stranded by a scale change — is skipped rather than
    // treated as position zero, which would silently paint the axis at its worst. An axis may also call a
    // status N/A that another axis doesn't, so exclusion is asked of this palette.
    if (!status || status.isExcludedFromRollup) {
      continue;
    }
    if (!worst || status.order < worst.order) {
      worst = status;
    }
  }
  return worst;
}

/**
 * "Not applicable" only when EVERY gating axis says so — N/A on one axis while another is live still leaves
 * the item scoreable. Mirrors `ItemRollup.IsItemExcluded`, and the sibling derivation in `instanceModel.ts`:
 * the gating axes are those marked required, or all of them when none are.
 */
function isItemExcluded(
  dimensions: GeneratedDimension[],
  statuses: ItemAxisLookup,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): boolean {
  const required = dimensions.filter((d) => d.requiredForCompletion);
  const gating = itemAxes(required.length > 0 ? required : dimensions, sectionKey, segmentKey, itemKey);
  return gating.length > 0 && gating.every((d) =>
    statusOn(d, statuses.get(itemStatusKeyOf(sectionKey, segmentKey, itemKey, d.key)))?.isExcludedFromRollup === true);
}

const statusOn = (dim: GeneratedDimension, statusKey: string | undefined): GeneratedStatus | undefined =>
  statusKey === undefined ? undefined : dim.palette.statuses.find((s) => s.key === statusKey);

const dotFor = (dim: GeneratedDimension, status: GeneratedStatus | undefined): AxisDot => ({
  key: dim.key,
  label: dim.label,
  statusName: status?.name ?? null,
  colour: status?.colour ?? UNSET_COLOUR,
  optional: dim.requiredForCompletion === false,
});
